import React, { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, Wallet, ShoppingCart, Receipt, HandCoins, CreditCard,
  Landmark, BookOpen, Warehouse, PiggyBank, Calculator, FileBarChart2,
  CheckCircle2, Files, ScrollText, Database, Users, Settings, ArrowRightLeft,
  Package, Briefcase, Building2, FileText, Crown,
  LogOut, Menu, X, Bell, ChevronDown, ChevronRight, UserCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

// Each item: { code, label, icon, path, enabled }
// MENU_GROUPS is purely a presentational grouping — all routes, permissions,
// and page components remain unchanged. Order follows the user's business flow.
const MENU_GROUPS = [
  {
    id: "utama", label: "Utama", icon: LayoutDashboard,
    items: [
      { code: "dasbor", label: "Dasbor", icon: LayoutDashboard, path: "/dasbor", enabled: true },
      { code: "executive-dashboard", label: "Dashboard Eksekutif", icon: Crown, path: "/executive-dashboard", enabled: true },
      { code: "approval-center", label: "Approval Center", icon: CheckCircle2, path: "/approval-center", enabled: true },
      { code: "persetujuan", label: "Persetujuan", icon: CheckCircle2, path: "/persetujuan", enabled: true },
      { code: "notifikasi", label: "Notifikasi", icon: Bell, path: "/notifikasi", enabled: true },
    ],
  },
  {
    id: "finance", label: "Keuangan & Akuntansi", icon: Landmark,
    items: [
      { code: "keuangan", label: "Keuangan", icon: Wallet, path: "/keuangan", enabled: true },
      { code: "akuntansi", label: "Akuntansi", icon: BookOpen, path: "/akuntansi", enabled: true },
      { code: "kas-bank", label: "Kas & Bank", icon: Landmark, path: "/kas-bank", enabled: true },
      { code: "piutang", label: "Piutang", icon: HandCoins, path: "/piutang", enabled: true },
      { code: "hutang", label: "Hutang", icon: CreditCard, path: "/hutang", enabled: true },
      { code: "bank-7a", label: "Rekonsiliasi Bank", icon: Landmark, path: "/bank", enabled: true },
      { code: "migrasi-saldo-awal", label: "Migrasi Saldo Awal", icon: ArrowRightLeft, path: "/akuntansi/migrasi-saldo-awal", enabled: true },
      { code: "anggaran", label: "Anggaran & Forecast", icon: PiggyBank, path: "/tahap7d", enabled: true },
      { code: "fin-7b", label: "Keuangan & Kontrol", icon: FileBarChart2, path: "/fin", enabled: true },
      { code: "perpajakan", label: "Perpajakan", icon: Calculator, path: "/perpajakan", enabled: true },
    ],
  },
  {
    id: "ops", label: "Penjualan & Operasional", icon: ShoppingCart,
    items: [
      { code: "penjualan", label: "Penjualan", icon: ShoppingCart, path: "/penjualan", enabled: true },
      { code: "pembelian", label: "Pembelian", icon: Receipt, path: "/pembelian", enabled: true },
      { code: "persediaan", label: "Persediaan", icon: Package, path: "/persediaan", enabled: true },
      { code: "klaim", label: "Klaim", icon: Receipt, path: "/klaim", enabled: true },
    ],
  },
  {
    id: "aset", label: "Aset", icon: Building2,
    items: [
      { code: "aset", label: "Aset", icon: Warehouse, path: "/akuntansi/aset", enabled: true },
      { code: "aset-7b", label: "Aset Tetap", icon: Warehouse, path: "/aset/register", enabled: true },
    ],
  },
  {
    id: "hr", label: "HR & Payroll", icon: Briefcase,
    items: [
      { code: "hr", label: "HR & Admin", icon: Users, path: "/hr", enabled: true },
      { code: "payroll", label: "Payroll", icon: Wallet, path: "/payroll", enabled: true },
      { code: "recruitment", label: "Recruitment", icon: HandCoins, path: "/recruitment", enabled: true },
      { code: "onboarding", label: "Onboarding", icon: Users, path: "/onboarding", enabled: true },
      { code: "offboarding", label: "Offboarding", icon: LogOut, path: "/offboarding", enabled: true },
      { code: "ess", label: "Portal Karyawan (ESS)", icon: UserCircle, path: "/ess", enabled: true },
      { code: "mss", label: "Manager (MSS)", icon: Users, path: "/mss", enabled: true },
      { code: "hrd-dashboard", label: "Dashboard HRD", icon: LayoutDashboard, path: "/hrd-dashboard", enabled: true },
      { code: "admin-dashboard", label: "Dashboard Admin", icon: LayoutDashboard, path: "/admin-dashboard", enabled: true },
      { code: "dokumen-hr", label: "Dokumen HR", icon: Files, path: "/dokumen-hr", enabled: true },
      { code: "laporan-hr", label: "Laporan HR", icon: FileBarChart2, path: "/laporan-hr", enabled: true },
      { code: "password-reset-admin", label: "Reset Password (Admin)", icon: UserCircle, path: "/password-reset-admin", enabled: true },
    ],
  },
  {
    id: "reports", label: "Laporan, Dokumen & Audit", icon: FileText,
    items: [
      { code: "laporan", label: "Laporan", icon: FileBarChart2, path: "/laporan", enabled: true },
      { code: "ekspor-7c", label: "Ekspor Laporan", icon: Files, path: "/ekspor", enabled: true },
      { code: "dokumen", label: "Dokumen", icon: Files, path: "/dokumen", enabled: true },
      { code: "executive-reports", label: "Arsip Laporan Eksekutif", icon: FileText, path: "/executive-reports", enabled: true },
      { code: "executive-digest", label: "Digest Eksekutif", icon: FileText, path: "/executive-digest", enabled: true },
      { code: "executive-alert-analytics", label: "Analitik Alert Eksekutif", icon: FileBarChart2, path: "/executive-alert-analytics", enabled: true },
      { code: "executive-forecast-history", label: "Riwayat Forecast", icon: FileBarChart2, path: "/executive-forecast-history", enabled: true },
      { code: "jejak-audit", label: "Jejak Audit", icon: ScrollText, path: "/jejak-audit", enabled: true },
      { code: "executive-audit", label: "Audit Eksekutif", icon: ScrollText, path: "/executive-audit", enabled: true },
    ],
  },
  {
    id: "admin", label: "Administrasi Sistem", icon: Settings,
    items: [
      { code: "data-master", label: "Data Master", icon: Database, path: "/data-master", enabled: true },
      { code: "manajemen-pengguna", label: "Manajemen Pengguna", icon: Users, path: "/manajemen-pengguna", enabled: true },
      { code: "executive-alert-settings", label: "Konfigurasi Alert Eksekutif", icon: Bell, path: "/executive-alert-settings", enabled: true },
      { code: "executive-threshold-changes", label: "Permintaan Threshold", icon: Bell, path: "/executive-threshold-changes", enabled: true },
      { code: "executive-targets", label: "Target Eksekutif", icon: Crown, path: "/executive-targets", enabled: true },
      { code: "executive-scenarios", label: "Perencanaan & Skenario", icon: Crown, path: "/executive-scenarios", enabled: true },
      { code: "executive-scenario-comparison", label: "Perbandingan Skenario", icon: Crown, path: "/executive-scenario-comparison", enabled: true },
      { code: "pengaturan", label: "Pengaturan", icon: Settings, path: "/pengaturan", enabled: true },
      { code: "pengaturan-demo", label: "Pengaturan Demo", icon: Settings, path: "/pengaturan-demo", enabled: true, demoOnly: true },
    ],
  },
];

// Flattened for active-path longest-match resolution
const ALL_MENU_ITEMS = MENU_GROUPS.flatMap((g) => g.items);

function Sidebar({ open, onClose }) {
  const location = useLocation();
  const { user: navUser } = useAuth();
  // ---- Active path (longest-match wins; prevents parent-vs-child dual highlight) ----
  const activePath = React.useMemo(() => {
    const pn = location.pathname;
    const matches = ALL_MENU_ITEMS
      .filter((m) => m.enabled)
      .map((m) => m.path)
      .filter((p) => pn === p || pn.startsWith(p + "/"));
    matches.sort((a, b) => b.length - a.length);
    return matches[0] || null;
  }, [location.pathname]);

  // Which group owns the active item (so we can auto-expand it).
  const activeGroupId = React.useMemo(() => {
    if (!activePath) return MENU_GROUPS[0].id;
    const g = MENU_GROUPS.find((gr) => gr.items.some((i) => i.path === activePath));
    return g ? g.id : MENU_GROUPS[0].id;
  }, [activePath]);

  // ---- Collapsible state (mobile = accordion single-open) ----
  const [openGroups, setOpenGroups] = useState(() => new Set([activeGroupId]));
  // Keep active group open as route changes.
  useEffect(() => {
    setOpenGroups((prev) => {
      if (prev.has(activeGroupId)) return prev;
      const next = new Set(prev);
      next.add(activeGroupId);
      // enforce max 2 open on desktop; auto-close oldest if needed
      const isMobile = typeof window !== "undefined" && window.innerWidth < 1024;
      if (isMobile) {
        return new Set([activeGroupId]);
      }
      if (next.size > 2) {
        // drop the first non-active entry
        for (const id of next) {
          if (id !== activeGroupId) { next.delete(id); break; }
        }
      }
      return next;
    });
  }, [activeGroupId]);

  const toggleGroup = (id) => {
    setOpenGroups((prev) => {
      const isMobile = typeof window !== "undefined" && window.innerWidth < 1024;
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
        return next;
      }
      if (isMobile) {
        return new Set([id]); // accordion
      }
      next.add(id);
      if (next.size > 2) {
        // drop the oldest group that is not the one just toggled
        for (const existing of next) {
          if (existing !== id) { next.delete(existing); break; }
        }
      }
      return next;
    });
  };

  const onItemKeyDown = () => {}; // placeholder for future keyboard shortcuts

  return (
    <>
      {open && <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={onClose} />}
      <aside
        className={`fixed lg:sticky top-0 left-0 z-40 h-screen w-72 bg-slate-900 text-slate-100 flex flex-col transition-transform ${open ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`}
        data-testid="sidebar"
      >
        <div className="h-16 flex items-center justify-between px-5 border-b border-slate-800">
          <div>
            <div className="font-display text-base font-bold tracking-tight leading-none">ABS Finance</div>
            <div className="text-[11px] text-slate-400 mt-1">Accounting &amp; Tax</div>
          </div>
          <button className="lg:hidden text-slate-300" onClick={onClose} aria-label="Tutup Menu">
            <X className="h-5 w-5" />
          </button>
        </div>
        <nav className="flex-1 overflow-y-auto py-3 px-2" aria-label="Menu Utama">
          {MENU_GROUPS.map((group) => {
            const GroupIcon = group.icon;
            const expanded = openGroups.has(group.id);
            const panelId = `sidebar-group-panel-${group.id}`;
            const headerId = `sidebar-group-header-${group.id}`;
            return (
              <div key={group.id} className="mb-1" data-testid={`sidebar-group-${group.id}`}>
                <button
                  id={headerId}
                  type="button"
                  onClick={() => toggleGroup(group.id)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggleGroup(group.id); }
                  }}
                  aria-expanded={expanded}
                  aria-controls={panelId}
                  data-testid={`sidebar-group-header-${group.id}`}
                  className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-md text-slate-400 hover:text-slate-100 hover:bg-slate-800/60 transition-colors"
                >
                  <span className="flex items-center gap-2.5">
                    <GroupIcon className="h-3.5 w-3.5 opacity-80" />
                    <span className="text-[11px] font-semibold tracking-[0.08em] uppercase">
                      {group.label}
                    </span>
                  </span>
                  <ChevronDown
                    className={`h-3.5 w-3.5 transition-transform ${expanded ? "rotate-0" : "-rotate-90"}`}
                    aria-hidden="true"
                  />
                </button>
                <div
                  id={panelId}
                  role="region"
                  aria-labelledby={headerId}
                  hidden={!expanded}
                  className="mt-0.5 ml-1.5 pl-2 border-l border-slate-800"
                >
                  {group.items.filter((item) => !item.demoOnly || navUser?.is_demo).map((item) => {
                    const Icon = item.icon;
                    if (!item.enabled) {
                      return (
                        <div
                          key={item.code}
                          className="flex items-center justify-between gap-3 px-3 py-2 rounded-md text-slate-500 cursor-not-allowed"
                          title="Segera Hadir"
                          data-testid={`sidebar-item-${item.code}`}
                        >
                          <span className="flex items-center gap-3">
                            <Icon className="h-4 w-4" />
                            <span className="text-sm">{item.label}</span>
                          </span>
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-slate-800 border-slate-700 text-slate-400">
                            Segera Hadir
                          </Badge>
                        </div>
                      );
                    }
                    const isActive = item.path === activePath;
                    return (
                      <NavLink
                        key={item.code}
                        to={item.path}
                        onClick={onClose}
                        onKeyDown={onItemKeyDown}
                        data-testid={`sidebar-item-${item.code}`}
                        aria-current={isActive ? "page" : undefined}
                        className={() =>
                          `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                            isActive ? "bg-emerald-600 text-white font-semibold" : "text-slate-200 hover:bg-slate-800 hover:text-white"
                          }`
                        }
                      >
                        <Icon className="h-4 w-4" />
                        <span>{item.label}</span>
                      </NavLink>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </nav>
        <div className="p-3 border-t border-slate-800 text-[11px] text-slate-500">
          v1.0.0 · PT Amaydelli Berkah Sejahtera
        </div>
      </aside>
    </>
  );
}

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sideOpen, setSideOpen] = useState(false);

  const initials = (user?.full_name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  const doLogout = async () => {
    await logout();
    toast.success("Anda telah keluar dari sistem.");
    navigate("/login");
  };

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar open={sideOpen} onClose={() => setSideOpen(false)} />
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="sticky top-0 z-20 bg-white border-b border-slate-200 h-16 flex items-center gap-4 px-4 sm:px-6">
          <button className="lg:hidden text-slate-700" onClick={() => setSideOpen(true)} aria-label="Buka Menu" data-testid="open-sidebar-button">
            <Menu className="h-5 w-5" />
          </button>
          <div className="flex-1">
            {user?.is_demo && (
              <div className="inline-flex items-center gap-2 rounded-md bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-800 ring-1 ring-amber-200">
                <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                MODE DEMO — Data pada halaman ini hanya data simulasi.
              </div>
            )}
          </div>
          <Button variant="ghost" size="icon" aria-label="Notifikasi" data-testid="notification-button">
            <Bell className="h-4 w-4" />
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-slate-100" data-testid="user-menu-button">
                <div className="h-8 w-8 rounded-full bg-slate-900 text-white grid place-items-center text-xs font-semibold">
                  {initials}
                </div>
                <div className="hidden sm:block text-left">
                  <div className="text-sm font-semibold leading-none">{user?.full_name}</div>
                  <div className="text-[11px] text-slate-500 mt-0.5">{user?.role_name}</div>
                </div>
                <ChevronDown className="h-4 w-4 text-slate-500" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 bg-white">
              <DropdownMenuLabel>{user?.email}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate("/profil")} data-testid="menu-profile">
                <UserCircle className="h-4 w-4 mr-2" /> Profil Saya
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/pengaturan")} data-testid="menu-settings">
                <Settings className="h-4 w-4 mr-2" /> Pengaturan
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={doLogout} className="text-rose-600 focus:text-rose-700" data-testid="menu-logout">
                <LogOut className="h-4 w-4 mr-2" /> Keluar
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>
        <main className="flex-1 p-4 sm:p-6 lg:p-8 page-fade">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
